import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";
import { useAuth } from "@/lib/AuthContext";
import "./OnboardingTour.css";

const STORAGE_KEY_CREATOR = "cinengage_onboarding_done_v1";
const STORAGE_KEY_SPONSOR = "cinengage_onboarding_sponsor_done_v1";

// Steps are declared as {id, titleKey, textKey, target?} — the tour builder
// wires them into Shepherd. Adding a role variant is a matter of appending a
// new list here without touching the imperative code below.
const TOUR_STEPS = {
  creator: {
    storageKey: STORAGE_KEY_CREATOR,
    keyPrefix: "tour",
    finishRoute: "/dashboard/proyectos/nuevo",
    finishKey: "tour.finish",
    steps: [
      { id: "welcome", titleKey: "step1_title", textKey: "step1_text" },
      { id: "new-project", titleKey: "step2_title", textKey: "step2_text", target: '[data-testid="button-new-project"]', on: "bottom" },
      { id: "matches", titleKey: "step3_title", textKey: "step3_text", target: '[data-testid="link-sidebar-matches"]', on: "right" },
      { id: "ai-analysis", titleKey: "step4_title", textKey: "step4_text", target: '[data-testid="link-sidebar-my_projects"]', on: "right" },
      { id: "share-sponsor", titleKey: "step5_title", textKey: "step5_text", target: '[data-testid="link-sidebar-negotiations"]', on: "right" },
      { id: "vitrina", titleKey: "step6_title", textKey: "step6_text", target: '[data-testid="link-dashboard-home"]', on: "bottom" },
    ],
  },
  sponsor: {
    storageKey: STORAGE_KEY_SPONSOR,
    keyPrefix: "tour.sponsor",
    finishRoute: "/dashboard/marcas",
    finishKey: "tour.sponsor.cta_finish",
    steps: [
      { id: "welcome", titleKey: "step1_title", textKey: "step1_text" },
      { id: "register-brand", titleKey: "step2_title", textKey: "step2_text", target: '[data-testid="link-sidebar-brands"]', on: "right" },
      { id: "publish-brief", titleKey: "step3_title", textKey: "step3_text", target: '[data-testid="link-sidebar-my_briefs"]', on: "right" },
      { id: "explore-matches", titleKey: "step4_title", textKey: "step4_text", target: '[data-testid="link-sidebar-recommendations"]', on: "right" },
      { id: "negotiate", titleKey: "step5_title", textKey: "step5_text", target: '[data-testid="link-sidebar-negotiations"]', on: "right" },
      { id: "featured-sponsor", titleKey: "step6_title", textKey: "step6_text", target: '[data-testid="link-dashboard-home"]', on: "bottom" },
    ],
  },
};

/**
 * OnboardingTour
 * - Auto-starts a Shepherd.js tour once for new creators (localStorage guard).
 * - Exposes window.__cinengageStartTour() so any component (e.g. topbar Help
 *   button) can restart the tour.
 * - i18n-aware: reads all step copy from tour.step*_title / tour.step*_text.
 *
 * Renders nothing.
 */
export default function OnboardingTour({ autoStart = true }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const tourRef = useRef(null);

  const startTour = useCallback(() => {
    // Tear down previous tour if any
    if (tourRef.current) {
      try { tourRef.current.complete(); } catch (_) { /* noop */ }
      tourRef.current = null;
    }

    const role = user?.role === "sponsor" ? "sponsor" : "creator";
    const config = TOUR_STEPS[role];
    const total = config.steps.length;
    const firstName = (user?.name || "").split(" ")[0] || "";

    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        scrollTo: { behavior: "smooth", block: "center" },
        cancelIcon: { enabled: true },
        classes: "cinengage-tour",
        modalOverlayOpeningPadding: 6,
        modalOverlayOpeningRadius: 12,
        arrow: true,
      },
    });

    const buttonsFor = (idx) => {
      const isFirst = idx === 1;
      const isLast = idx === total;
      const btns = [];
      if (!isFirst) {
        btns.push({
          text: t("tour.back"),
          classes: "shepherd-button-secondary",
          action: () => tour.back(),
        });
      }
      btns.push({
        text: t("tour.skip"),
        classes: "shepherd-button-skip",
        action: () => tour.complete(),
      });
      if (isLast) {
        btns.push({
          text: t(config.finishKey),
          classes: "shepherd-button-primary",
          action: () => {
            tour.complete();
            nav(config.finishRoute);
          },
        });
      } else {
        btns.push({
          text: t("tour.next"),
          classes: "shepherd-button-primary",
          action: () => tour.next(),
        });
      }
      return btns;
    };

    config.steps.forEach((s, i) => {
      const idx = i + 1;
      const titleI18nKey = `${config.keyPrefix}.${s.titleKey}`;
      const textI18nKey = `${config.keyPrefix}.${s.textKey}`;
      const title = idx === 1
        ? t(titleI18nKey, { name: firstName })
        : t(titleI18nKey);
      const text = `
        <p class="shepherd-step-index">${t("tour.step", { n: idx, total })}</p>
        <p class="shepherd-step-body">${t(textI18nKey)}</p>
      `;
      const opts = { id: s.id, title, text, buttons: buttonsFor(idx) };
      if (s.target) opts.attachTo = { element: s.target, on: s.on || "bottom" };
      tour.addStep(opts);
    });

    // Persist the "seen" flag when the tour is completed or dismissed.
    const markSeen = () => {
      try { localStorage.setItem(config.storageKey, "1"); } catch (_) { /* private mode */ }
    };
    tour.on("complete", markSeen);
    tour.on("cancel", markSeen);

    tourRef.current = tour;
    tour.start();
  }, [t, user?.name, user?.role, nav]);

  // Expose global restart handle so topbar can trigger it
  useEffect(() => {
    window.__cinengageStartTour = startTour;
    return () => {
      if (window.__cinengageStartTour === startTour) {
        delete window.__cinengageStartTour;
      }
    };
  }, [startTour]);

  // Auto-start once per role (creator + sponsor supported)
  useEffect(() => {
    if (!autoStart) return;
    if (!user) return;
    const config = user.role === "sponsor"
      ? TOUR_STEPS.sponsor
      : user.role === "creator"
        ? TOUR_STEPS.creator
        : null;
    if (!config) return;
    let done = "0";
    try { done = localStorage.getItem(config.storageKey) || "0"; } catch (_) { /* private mode */ }
    if (done === "1") return;
    // Delay so the target elements finish rendering
    const timer = setTimeout(startTour, 900);
    return () => clearTimeout(timer);
  }, [autoStart, user, startTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        try { tourRef.current.complete(); } catch (_) { /* already completed */ }
        tourRef.current = null;
      }
    };
  }, []);

  return null;
}
